/*
 * Cart drawer behaviour (sections/nl-cart-drawer.liquid). Loaded with defer from the section, outside the re-rendered
 * panel, after assets/nl-app.js. It builds on the foundation contract (THEME_ARCHITECTURE.md 8): <cart-drawer>
 * (dialog, focus trap via showModal, Esc, overlay click, focus return), NL.cart and the cart events.
 *
 *   line updates      <cart-drawer>.updateLine delegates to lineUpdateHook (set below): busy line + aria-busy panel,
 *                     spinner on the pressed control, controls and checkout disabled during the request, inline
 *                     errors from the cart API (422 stock limits, network) after a refresh, focus restored to the
 *                     same control (or the next line) after the Section Rendering API swaps the markup
 *   announcements     polite live region outside the re-rendered panel ("Nákupní košík: 3 produkty, celkem 947 Kč")
 *   scroll lock       html.nl-cdr-gutter pads the root by the lost scrollbar width while html overflow is hidden
 *   scroll shadows    .is-scrolled (header divider) and .has-more (footer shadow) on the panel
 *   <nl-w2-progress>  free shipping / gift progress, compositor only scaleX from the last shown value
 *   <nl-w2-note>      order note toggle, saved to /cart/update.js without a re-render
 *   motion            new lines slide in, changed quantities flash, removed lines fade, count bump, total tick,
 *                     upsell tiles stagger when the recommendation source changes
 *
 * Motion is transform / opacity only and is skipped under prefers-reduced-motion or when the motion setting is off.
 */
(() => {
  'use strict';

  if (window.NLW2Cart) return;
  window.NLW2Cart = { version: '2.0.0' };

  const NL = (window.NL = window.NL || {});
  const doc = document;
  const reduced = () =>
    NL.motion ? NL.motion.reduced : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const drawerEl = () => doc.querySelector('cart-drawer');
  const panelOf = (drawer) => drawer && drawer.querySelector('[data-cart-section]');
  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };
  const restartClass = (el, className) => {
    if (!el) return;
    el.classList.remove(className);
    void el.offsetWidth;
    el.classList.add(className);
  };
  const lineByKey = (root, key) =>
    root && key ? root.querySelector(`.nl-cart-line[data-line-key="${CSS.escape(String(key))}"]`) : null;
  const strings = () => NL.strings || {};

  const ALERT_ICON =
    '<svg class="nl-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>';

  /* ==========================================================================
     Scroll lock without layout shift
     ========================================================================== */

  // Classic scrollbars disappear when html gets overflow hidden (nl-components.css): the root gets the lost width
  // back as padding so the header and the page behind the overlay do not jump sideways
  let baseWidth = 0;
  const measureScrollbar = () => {
    if (doc.querySelector('.nl-dialog[open]')) return;
    baseWidth = doc.documentElement.clientWidth;
  };
  const lockGutter = () => {
    const root = doc.documentElement;
    const lost = baseWidth ? root.clientWidth - baseWidth : 0;
    if (lost > 0 && lost < 40) {
      root.style.setProperty('--nl-cdr-sb', `${lost}px`);
      root.classList.add('nl-cdr-gutter');
    }
  };
  const unlockGutter = () => {
    doc.documentElement.classList.remove('nl-cdr-gutter');
    doc.documentElement.style.removeProperty('--nl-cdr-sb');
  };

  /* ==========================================================================
     Live announcements
     ========================================================================== */

  let announceTimer = 0;
  const announce = (drawer, prefix) => {
    if (!drawer) return;
    const live = drawer.querySelector('[data-cart-drawer-live]');
    if (!live) return;
    window.clearTimeout(announceTimer);
    live.textContent = '';
    // Read the message after the re-render settles, one announcement per burst of events
    announceTimer = window.setTimeout(() => {
      const source = drawer.querySelector('[data-cart-drawer-message]');
      if (!source) return;
      live.textContent = [prefix, source.textContent.replace(/\s+/g, ' ').trim()].filter(Boolean).join('. ');
    }, 160);
  };

  /* ==========================================================================
     Scroll shadows
     ========================================================================== */

  const syncScroll = (drawer) => {
    const panel = panelOf(drawer);
    const body = panel && panel.querySelector('[data-cart-drawer-body]');
    if (!panel || !body) return;
    const max = body.scrollHeight - body.clientHeight;
    panel.classList.toggle('is-scrolled', body.scrollTop > 2);
    panel.classList.toggle('has-more', max > 2 && body.scrollTop < max - 2);
  };

  /* ==========================================================================
     Inline errors
     ========================================================================== */

  const errorState = { key: null, message: '' };

  const clearError = (drawer) => {
    errorState.key = null;
    errorState.message = '';
    if (drawer) drawer.querySelectorAll('.nl-cdr__error').forEach((el) => el.remove());
  };

  const showError = (drawer) => {
    if (!drawer || !errorState.message) return;
    drawer.querySelectorAll('.nl-cdr__error').forEach((el) => el.remove());
    const box = doc.createElement('p');
    box.className = 'nl-cdr__error';
    box.setAttribute('role', 'alert');
    box.innerHTML = ALERT_ICON;
    const text = doc.createElement('span');
    text.textContent = errorState.message;
    box.append(text);
    const line = lineByKey(drawer, errorState.key);
    const slot = (line && line.querySelector('[data-line-error-slot]')) || drawer.querySelector('[data-cart-drawer-alert]');
    if (!slot) return;
    slot.append(box);
    if (box.scrollIntoView) box.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
  };

  /* ==========================================================================
     Busy state and focus restore for line updates
     ========================================================================== */

  let busyCount = 0;
  const pressed = { key: null, kind: null };

  const kindOf = (control) => {
    if (!control) return 'input';
    if (control.matches('[data-cart-remove]')) return 'remove';
    if (control.matches('[data-qty-step="1"]')) return 'plus';
    if (control.matches('[data-qty-step="-1"]')) return 'minus';
    return 'input';
  };

  const controlOf = (line, kind) => {
    if (!line) return null;
    if (kind === 'remove') return line.querySelector('[data-cart-remove]');
    if (kind === 'plus') return line.querySelector('[data-qty-step="1"]');
    if (kind === 'minus') return line.querySelector('[data-qty-step="-1"]');
    return line.querySelector('[data-line-quantity]');
  };

  const setCheckoutBusy = (drawer, busy) => {
    const checkout = drawer && drawer.querySelector('[data-cart-checkout]');
    if (!checkout) return;
    if (busy) checkout.setAttribute('aria-disabled', 'true');
    else checkout.removeAttribute('aria-disabled');
  };

  const setLineBusy = (line, kind, busy) => {
    if (!line) return;
    line.classList.toggle('is-busy', busy);
    if (busy) line.setAttribute('aria-busy', 'true');
    else line.removeAttribute('aria-busy');
    line.querySelectorAll('button').forEach((button) => {
      if (busy) {
        button.dataset.w2WasDisabled = button.disabled ? '1' : '';
        button.disabled = true;
      } else if (button.dataset.w2WasDisabled !== undefined) {
        button.disabled = button.dataset.w2WasDisabled === '1';
        delete button.dataset.w2WasDisabled;
      }
    });
    const input = line.querySelector('[data-line-quantity]');
    if (input) input.readOnly = busy;
    const control = controlOf(line, kind);
    if (control && control.tagName === 'BUTTON') control.classList.toggle('is-pressed', busy);
  };

  const focusInDrawer = (drawer, element) => {
    if (!element || !drawer || !drawer.isOpen) return;
    element.focus({ preventScroll: true });
    if (element.scrollIntoView) element.scrollIntoView({ block: 'nearest' });
  };

  const restoreFocus = (drawer, key, kind, index, hadFocus) => {
    if (!drawer || !drawer.isOpen) return;
    // Restore when the visitor was on a control, or when focus fell out of the dialog (a stepper button that turns
    // disabled at 0 drops focus to the body before the request even starts)
    const active = doc.activeElement;
    const lost = !active || active === doc.body || !drawer.contains(active);
    if (!hadFocus && !lost) return;
    const line = lineByKey(drawer, key);
    if (line) {
      let target = controlOf(line, kind);
      if (!target || target.disabled) target = controlOf(line, 'input');
      focusInDrawer(drawer, target);
      return;
    }
    // The line is gone: next line (same position), else the previous one, else the drawer title
    const lines = Array.from(drawer.querySelectorAll('.nl-cart-line'));
    const next = lines[Math.min(index, lines.length - 1)];
    const title = next && next.querySelector('.nl-cart-line__title');
    focusInDrawer(drawer, title || drawer.querySelector('[data-cart-drawer-focus]'));
  };

  async function lineUpdateHook(key, quantity, source) {
    const drawer = this;
    const panel = panelOf(drawer);
    const line = lineByKey(drawer, key);
    const kind = pressed.key === key && pressed.kind ? pressed.kind : kindOf(source);
    const lines = Array.from(drawer.querySelectorAll('.nl-cart-line'));
    const index = Math.max(0, lines.indexOf(line));
    const hadFocus = drawer.contains(doc.activeElement) && doc.activeElement !== drawer.querySelector('[data-cart-drawer-focus]');
    pressed.key = null;
    pressed.kind = null;

    clearError(drawer);
    busyCount += 1;
    if (panel) panel.setAttribute('aria-busy', 'true');
    setCheckoutBusy(drawer, true);
    setLineBusy(line, kind, true);

    try {
      await NL.cart.change(key, quantity, { source: 'cart-drawer' });
      restoreFocus(drawer, key, kind, index, hadFocus);
    } catch (error) {
      errorState.key = key;
      errorState.message = (error && error.message) || strings().errorGeneric || '';
      try {
        await NL.cart.refresh('cart-drawer-error');
      } catch (refreshError) {
        /* offline: keep the current markup, the busy state is cleared below */
      }
      showError(drawer);
      restoreFocus(drawer, key, kind, index, hadFocus);
    } finally {
      busyCount = Math.max(0, busyCount - 1);
      if (busyCount === 0) {
        const current = panelOf(drawer);
        if (current) current.removeAttribute('aria-busy');
        setCheckoutBusy(drawer, false);
      }
      if (line && line.isConnected) setLineBusy(line, kind, false);
    }
  }

  const installHook = () => {
    const CartDrawer = customElements.get('cart-drawer');
    if (CartDrawer && !CartDrawer.prototype.lineUpdateHook) CartDrawer.prototype.lineUpdateHook = lineUpdateHook;
  };
  if (customElements.get('cart-drawer')) installHook();
  else customElements.whenDefined('cart-drawer').then(installHook);

  // Remember which control started the update (the change event arrives later, debounced, from the input)
  doc.addEventListener(
    'click',
    (event) => {
      const control = event.target.closest && event.target.closest('cart-drawer .nl-cart-line :is([data-qty-step], [data-cart-remove])');
      if (!control) return;
      const line = control.closest('.nl-cart-line');
      pressed.key = line ? line.dataset.lineKey : null;
      pressed.kind = kindOf(control);
    },
    true
  );

  // No checkout while the cart is still saving
  doc.addEventListener(
    'submit',
    (event) => {
      const form = event.target.closest && event.target.closest('cart-drawer [data-cart-drawer-form]');
      if (!form) return;
      const checkout = form.querySelector('[data-cart-checkout]');
      if (busyCount > 0 || (checkout && checkout.getAttribute('aria-disabled') === 'true')) {
        event.preventDefault();
        return;
      }
      if (checkout) checkout.setAttribute('aria-busy', 'true');
    },
    true
  );

  // Errors from other cart actions while the drawer is open (upsell add, bundle): shown inline, toasts sit behind the
  // modal overlay
  doc.addEventListener('cart:error', (event) => {
    const drawer = drawerEl();
    const detail = event.detail || {};
    if (!drawer || !drawer.isOpen || detail.source === 'cart-drawer') return;
    errorState.key = null;
    errorState.message = detail.message || strings().cartError || '';
    showError(drawer);
  });

  /* ==========================================================================
     <nl-w2-progress>
     ========================================================================== */

  const shownProgress = new Map();
  const FILL_MS = 900;

  class W2Progress extends HTMLElement {
    connectedCallback() {
      this.fill = this.querySelector('[data-w2-fill]');
      if (!this.fill) return;
      this.key = this.dataset.key || 'default';
      this.target = Math.max(0, Math.min(1, (parseFloat(this.dataset.value) || 0) / 100));

      if (reduced()) {
        this.set(this.target, true);
        shownProgress.set(this.key, this.target);
        return;
      }

      const from = shownProgress.has(this.key) ? shownProgress.get(this.key) : 0;
      const dialog = this.closest('dialog');
      if (!dialog || dialog.open) {
        this.play(from);
      } else {
        // Drawer closed: hold the last shown value, animate when the drawer opens
        this.set(from, true);
        this.from = from;
        this.pending = true;
      }
    }

    set(value, instant) {
      this.fill.classList.toggle('is-instant', Boolean(instant));
      this.fill.style.setProperty('--w2-p', String(value));
    }

    play(fromValue) {
      if (!this.fill) return;
      const from = typeof fromValue === 'number' ? fromValue : this.from || 0;
      const to = this.target;
      this.pending = false;
      shownProgress.set(this.key, to);

      if (reduced() || Math.abs(to - from) < 0.002) {
        this.set(to, true);
        return;
      }

      const crossing = Array.from(this.querySelectorAll('[data-w2-mark]')).filter((mark) => {
        const pos = parseFloat(mark.dataset.w2Mark);
        return to > from && pos > from && pos <= to + 0.0001;
      });
      crossing.forEach((mark) => mark.classList.remove('is-reached', 'is-pop'));

      // Commit the start value with a forced style flush, then set the target in the same task
      this.set(from, true);
      void this.fill.getBoundingClientRect();
      this.set(to, false);
      if (to > from) restartClass(this, 'is-gaining');
      crossing.forEach((mark) => {
        const pos = parseFloat(mark.dataset.w2Mark);
        const share = Math.min(1, (pos - from) / (to - from));
        window.setTimeout(() => {
          mark.classList.add('is-reached');
          restartClass(mark, 'is-pop');
        }, 160 + FILL_MS * share * 0.62);
      });
    }
  }

  define('nl-w2-progress', W2Progress);

  /* ==========================================================================
     <nl-w2-note>
     ========================================================================== */

  const note = { open: null, value: null, saved: null, timer: 0 };

  class W2Note extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this.toggleBtn = this.querySelector('[data-w2-note-toggle]');
      this.panel = this.querySelector('[data-w2-note-panel]');
      this.input = this.querySelector('[data-w2-note-input]');
      this.status = this.querySelector('[data-w2-note-status]');
      if (!this.toggleBtn || !this.panel || !this.input) return;
      this._bound = true;

      if (note.saved === null) note.saved = this.input.value;
      // Keep the visitor's open state and latest text across drawer re-renders
      if (note.value !== null && note.value !== this.input.value) this.input.value = note.value;
      if (note.open !== null) this.setOpen(note.open, false);

      this.toggleBtn.addEventListener('click', () => this.setOpen(this.panel.hidden, true));
      this.input.addEventListener('input', () => {
        note.value = this.input.value;
        this.queue();
      });
      this.input.addEventListener('change', () => this.flush());
    }

    setOpen(open, interactive) {
      note.open = open;
      this.panel.hidden = !open;
      this.classList.toggle('is-open', open);
      this.toggleBtn.setAttribute('aria-expanded', String(open));
      if (open && interactive) {
        if (!reduced()) {
          this.panel.classList.add('is-entering');
          void this.panel.offsetWidth;
          this.panel.classList.remove('is-entering');
        }
        this.input.focus({ preventScroll: true });
        if (this.panel.scrollIntoView) this.panel.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' });
      }
      const drawer = drawerEl();
      if (drawer) window.requestAnimationFrame(() => syncScroll(drawer));
    }

    queue() {
      window.clearTimeout(note.timer);
      this.showStatus('');
      note.timer = window.setTimeout(() => this.flush(), 700);
    }

    async flush() {
      window.clearTimeout(note.timer);
      const value = this.input.value;
      if (value === note.saved) return;
      const previous = note.saved;
      note.saved = value;
      try {
        const base = (NL.routes && NL.routes.cartUpdate) || '/cart/update';
        const response = await fetch(`${base}.js`, {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note: value }),
        });
        if (!response.ok) throw new Error(String(response.status));
        const current = doc.querySelector('nl-w2-note');
        if (current && typeof current.showStatus === 'function') current.showStatus(current.dataset.saved || '');
      } catch (error) {
        note.saved = previous;
      }
    }

    showStatus(text) {
      if (!this.status) return;
      window.clearTimeout(this.statusTimer);
      this.status.textContent = text;
      this.status.classList.toggle('is-visible', Boolean(text));
      if (text) {
        this.statusTimer = window.setTimeout(() => this.status.classList.remove('is-visible'), 2600);
      }
    }
  }

  define('nl-w2-note', W2Note);

  /* ==========================================================================
     Re-render diff: lines, count, total, upsell
     ========================================================================== */

  const snap = { keys: null, qty: new Map(), count: null, total: null, upsell: null };
  const linesOf = (root) => Array.from(root.querySelectorAll('[data-cart-drawer-lines] > [data-line-key]'));
  const qtyOf = (line) => parseInt(line.dataset.quantity, 10) || 0;
  const textOf = (root, selector) => {
    const el = root.querySelector(selector);
    return el ? el.textContent.replace(/\s+/g, ' ').trim() : null;
  };
  const countOf = (root) => {
    const panel = panelOf(root);
    return panel ? parseInt(panel.dataset.itemCount, 10) || 0 : 0;
  };

  const takeSnapshot = () => {
    const drawer = drawerEl();
    if (!drawer) return;
    const lines = linesOf(drawer);
    snap.keys = new Set(lines.map((line) => line.dataset.lineKey));
    snap.qty = new Map(lines.map((line) => [line.dataset.lineKey, qtyOf(line)]));
    snap.total = textOf(drawer, '[data-cart-drawer-total]');
    snap.count = countOf(drawer);
  };

  const markUpsell = (drawer) => {
    const box = drawer.querySelector('[data-cart-upsell]');
    if (!box) return;
    const id = box.dataset.productId || '';
    if (id && id !== snap.upsell) {
      snap.upsell = id;
      if (!reduced()) box.classList.add('is-w2-enter');
    }
  };

  let addedPrefix = '';

  const onCartUpdated = (event) => {
    const drawer = drawerEl();
    if (!drawer) return;
    const detail = event.detail || {};
    const cart = detail.cart;

    if (!reduced() && snap.keys) {
      linesOf(drawer).forEach((line, index) => {
        const key = line.dataset.lineKey;
        if (!snap.keys.has(key)) {
          line.style.setProperty('--w2-i', String(index));
          line.classList.add('nl-w2-line-in');
        } else if (qtyOf(line) !== snap.qty.get(key)) {
          line.classList.add('nl-w2-line-flash');
        }
      });

      const count = cart && typeof cart.item_count === 'number' ? cart.item_count : null;
      if (count !== null && snap.count !== null && count > snap.count) {
        restartClass(drawer.querySelector('.nl-cdr__count'), 'nl-w2-bump');
      }

      const total = textOf(drawer, '[data-cart-drawer-total]');
      if (snap.total !== null && total !== null && total !== snap.total) {
        restartClass(drawer.querySelector('[data-cart-drawer-total]'), 'nl-w2-tick');
      }
    }

    takeSnapshot();
    if (drawer.isOpen) {
      markUpsell(drawer);
      if (detail.source !== 'pageshow') {
        announce(drawer, addedPrefix);
        addedPrefix = '';
      }
      window.requestAnimationFrame(() => syncScroll(drawer));
    }
  };

  // Removed lines fade out while the request runs (the re-render then drops them; a failed request re-renders them)
  const fadeOut = (line) => {
    if (!line || reduced()) return;
    line.classList.add('nl-w2-line-out');
  };

  doc.addEventListener(
    'click',
    (event) => {
      const remove = event.target.closest && event.target.closest('cart-drawer [data-cart-remove]');
      if (remove) fadeOut(remove.closest('.nl-cart-line'));
    },
    true
  );

  doc.addEventListener('change', (event) => {
    const input = event.target.closest && event.target.closest('cart-drawer [data-line-quantity]');
    if (input && (parseInt(input.value, 10) || 0) === 0) fadeOut(input.closest('.nl-cart-line'));
  });

  // Upsell source: the product added last; announce the add; keep focus inside the open drawer
  doc.addEventListener('cart:item-added', (event) => {
    const drawer = drawerEl();
    if (!drawer) return;
    const items = (event.detail && event.detail.items) || [];
    const first = items[0];
    clearError(drawer);
    addedPrefix = strings().cartAdded || '';

    if (drawer.isOpen) {
      // Added from inside the drawer (upsell): the pressed button was re-rendered away, move focus to the new line
      const active = doc.activeElement;
      if (!active || active === doc.body || !drawer.contains(active)) {
        const line = first && lineByKey(drawer, first.key);
        focusInDrawer(drawer, (line && line.querySelector('.nl-cart-line__title')) || drawer.querySelector('[data-cart-drawer-focus]'));
      }
      announce(drawer, addedPrefix);
      addedPrefix = '';
    }

    if (!first || !first.product_id) return;
    const box = drawer.querySelector('[data-cart-upsell]');
    if (!box) return;
    const id = String(first.product_id);
    if (box.dataset.productId === id) return;
    box.dataset.productId = id;
    if (drawer.isOpen && !box.dataset.loading && typeof drawer.loadUpsell === 'function') {
      drawer.loadUpsell();
      markUpsell(drawer);
    }
  });

  doc.addEventListener('dialog:open', (event) => {
    const drawer = event.target;
    if (!drawer || !drawer.matches || !drawer.matches('cart-drawer')) return;
    lockGutter();
    drawer.querySelectorAll('nl-w2-progress').forEach((progress) => {
      if (progress.pending && typeof progress.play === 'function') progress.play();
    });
    markUpsell(drawer);
    if (addedPrefix) {
      announce(drawer, addedPrefix);
      addedPrefix = '';
    }
    window.requestAnimationFrame(() => syncScroll(drawer));
  });

  // One shot classes must not replay when the closed dialog is shown again (display none restarts CSS animations)
  const TRANSIENT = ['nl-w2-line-in', 'nl-w2-line-flash', 'nl-w2-bump', 'nl-w2-tick', 'is-w2-enter', 'is-pop', 'is-gaining'];
  doc.addEventListener('dialog:close', (event) => {
    const drawer = event.target;
    if (!drawer || !drawer.matches || !drawer.matches('cart-drawer')) return;
    unlockGutter();
    measureScrollbar();
    clearError(drawer);
    TRANSIENT.forEach((name) => drawer.querySelectorAll(`.${name}`).forEach((el) => el.classList.remove(name)));
  });

  // Scroll shadows follow the body scroll (scroll does not bubble: capture on the drawer host)
  const bindScroll = () => {
    const drawer = drawerEl();
    if (!drawer || drawer._w2Scroll) return;
    drawer._w2Scroll = true;
    drawer.addEventListener(
      'scroll',
      (event) => {
        if (event.target.matches && event.target.matches('[data-cart-drawer-body]')) syncScroll(drawer);
      },
      { capture: true, passive: true }
    );
  };

  doc.addEventListener('cart:updated', onCartUpdated);
  doc.addEventListener('shopify:section:load', () => {
    takeSnapshot();
    bindScroll();
  });
  window.addEventListener('resize', () => {
    measureScrollbar();
    const drawer = drawerEl();
    if (drawer && drawer.isOpen) syncScroll(drawer);
  });

  const init = () => {
    takeSnapshot();
    bindScroll();
    measureScrollbar();
  };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  window.addEventListener('load', measureScrollbar, { once: true });
})();
